import React, { useCallback, useRef } from "react"
import {
  Table,
  Button,
  message,
} from "antd"
import styled from "styled-components"
import Select from "react-select"
import ReferralDashboardContainer from "container/ReferralDashboard"
import globalPagesContainer from "container/BrokerDashboard/globalPagesContainer"
import { referralSerializer, REFERRAL_STATUSES } from "./Utils"

const Wrap = styled.div`
  padding-top: 17px;
  overflow: scroll;
  width: 100%;
  .infoTable {
    th, td {
      border: none;
      font-family: "Rubik", sans-serif;
    }

    th {
      padding: 5px 5px 5px 18px;
    }

    td {
      padding: 10px 5px 10px 18px;
      background: #fafafa;

      &:hover {
        background: #fcfcfc;
      }
    }

    .ant-table-thead tr th {
      background: linear-gradient(0deg, #E5E5E5, #E5E5E5),
      linear-gradient(0deg, #E6E6E6, #E6E6E6);
    }
  }
`

const StatusSelectWrap = styled.div`
  min-width: 200px;
  max-width: 280px;
`

const buildReferralsListPayload = (filterQueries = {}) => {
  const payload = {
    skip: 0,
    limit: 25,
    sort_by: "created_at",
    sort_order: "desc",
  }
  if (filterQueries.status) payload.status = filterQueries.status
  if (filterQueries.search) payload.search = filterQueries.search
  return payload
}

const normalizeReferralStatus = (status) => {
  if (status == null) return ""
  return String(status).trim().toLowerCase().replace(/-/g, "_")
}

const getReferralStatusSelectValue = (referralOptions, record) => {
  if (!referralOptions || referralOptions.length === 0) return null
  if (record.brokerageDisplayName) {
    const found = referralOptions.find(x => x.value === record.brokerageDisplayName)
    if (found) return found
  }
  if (record.status) {
    const found = referralOptions.find(x => x.value === record.status)
    if (found) return found
  }
  if (record.currentStep) {
    const found = referralOptions.find(x => x.value === record.currentStep)
    if (found) return found
  }
  if (record.displayStatus && record.displayStatus !== "N/A") {
    const found = referralOptions.find(x => x.label === record.displayStatus)
    if (found) return found
  }
  return null
}

const ReferralsTable = (props) => {
  const {
    referrals,
    fetchingList,
    toggleReferralDrawer,
    getReferralDetails,
    updateReferralStatus,
    updateReferralStatusResponse,
    filterQueries,
  } = props || {}

  const filterQueriesRef = useRef({})
  filterQueriesRef.current = filterQueries || {}

  const statusHandlersRef = useRef({})
  statusHandlersRef.current = {
    updateReferralStatus,
  }

  const statusUpdateBusy = !!(updateReferralStatusResponse && updateReferralStatusResponse.isFetching)

  const handleReferralContractStatusChange = useCallback((record, selectedValue) => {
    if (!selectedValue) return
    if (!record.referralId) return
    if (!window.confirm("Are you sure?")) return

    const nextStatus = selectedValue.value
    const { updateReferralStatus: updateStatus } = statusHandlersRef.current

    updateStatus({
      referralId: record.referralId,
      status: nextStatus,
      notes: "",
      generate_contract: true,
      refreshPayload: buildReferralsListPayload(filterQueriesRef.current),
    })
  }, [])

  const handleEdit = (record) => {
    const agentId = record.agentId || record.primaryAgentId
    const referralId = record.referralId
    if (!referralId) return

    const isPreEscrow = normalizeReferralStatus(record.status) === "pre_escrow"

    if (!isPreEscrow) {
      const dealId = record.dealId
      if (agentId && dealId) {
        window.location.assign(
          `/broker/dashboard/${agentId}/deals/view/${dealId}?fromContract`,
        )
        return
      }
      message.warning("This referral is not linked to a deal yet.")
      return
    }

    toggleReferralDrawer({
      bool: true,
      data: {
        referral_id: referralId,
        primaryAgentId: agentId,
        agent_id: agentId,
        status: record.status,
        referralName: record.referralName,
        isNew: false,
      },
    })

    getReferralDetails({ referralId, agentId })
  }

  const renderStatusCell = (record) => {
    const value = getReferralStatusSelectValue(REFERRAL_STATUSES, record)

    return (
      <StatusSelectWrap onClick={e => e.stopPropagation()}>
        <Select
          classNamePrefix="referral-status-select"
          options={REFERRAL_STATUSES}
          value={value}
          openMenuOnClick={e => {
            e.stopPropagation()
          }}
          onChange={selected => handleReferralContractStatusChange(record, selected)}
          isDisabled={!!fetchingList || statusUpdateBusy}
          menuPlacement="auto"
        />
      </StatusSelectWrap>
    )
  }

  const columns = [
    {
      title: "Referral Name",
      dataIndex: "referralName",
      key: "referralName",
    },
    {
      title: "Property Address",
      dataIndex: "propertyAddress",
      key: "propertyAddress",
    },
    {
      title: "Referring Agent",
      dataIndex: "referringAgentName",
      key: "referringAgentName",
    },
    {
      title: "Agent Email",
      dataIndex: "agentEmail",
      key: "agentEmail",
    },
    {
      title: "Client Name",
      dataIndex: "clientName",
      key: "clientName",
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => renderStatusCell(record),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
    },
    {
      title: "Last Updated",
      dataIndex: "lastUpdated",
      key: "lastUpdated",
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text, record) => (
        <Button
          type="primary"
          onClick={() => handleEdit(record)}
        >
          Edit
        </Button>
      ),
    },
  ]

  const serialized = !fetchingList && referralSerializer(referrals) || []

  return (
    <Wrap>
      <Table
        className="infoTable"
        columns={columns}
        dataSource={serialized}
        loading={fetchingList}
        pagination={{
          defaultPageSize: 25,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50"],
        }}
      />
    </Wrap>
  )
}

export default ReferralDashboardContainer(globalPagesContainer(ReferralsTable))
